import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import {
  SEARCH_PREMIUM_FORMULA,
  SEARCH_RESULTS_LIMIT,
  SearchPackageType,
} from '@/consts/search';
import { SHOP_ITEMS_SEARCH_URL } from '@/consts/urls';
import { getContext } from '@/context';
import { PreviewMediaType } from '@/types';

import type { SearchResultItem, SearchResults } from '@/types';

interface SearchApiItem {
  id: number;
  title?: string;
  url?: string;
  data_url?: string;
  width?: number;
  height?: number;
  source_info?: {
    app_payload?: {
      presetId?: string;
      genaicategory?: string;
    };
  };
}

interface SearchApiResponse {
  response?: SearchApiItem[];
  metadata?: {
    restrictionInfo?: {
      restricted_type?: string;
      message?: { text?: string };
    };
  };
}

const MEDIA_TYPE_BY_PACKAGE: Record<SearchPackageType, PreviewMediaType> = {
  [SearchPackageType.Photo]: PreviewMediaType.IMAGE,
  [SearchPackageType.Video]: PreviewMediaType.VIDEO,
};

// Items without a usable source are unrenderable — drop them rather than
// letting a broken tile through.
const toSearchResultItem = (
  item: SearchApiItem,
  packageType: SearchPackageType
): SearchResultItem | null => {
  if (!item.url || !item.data_url) return null;

  const payload = item.source_info?.app_payload;

  return {
    id: String(item.id),
    title: item.title?.trim() ?? '',
    // From the request, not `item.type`: photos come back as `background`.
    mediaType: MEDIA_TYPE_BY_PACKAGE[packageType],
    posterUrl: item.url,
    mediaUrl: item.data_url,
    width: item.width ?? 0,
    height: item.height ?? 0,
    presetSlug: payload?.presetId ?? null,
    categorySlug: payload?.genaicategory ?? null,
  };
};

interface FetchPackageParams {
  query: string;
  packageType: SearchPackageType;
  signal?: AbortSignal;
}

const fetchPackage = async ({
  query,
  packageType,
  signal,
}: FetchPackageParams): Promise<SearchResults> => {
  const params = new URLSearchParams({
    premium_formula_name: SEARCH_PREMIUM_FORMULA,
    source: MINIAPP_PACKAGE_ID,
    package_type: packageType,
    q: query,
    limit: String(SEARCH_RESULTS_LIMIT),
  });

  const response = await getContext().utils.api.fetch(
    `${SHOP_ITEMS_SEARCH_URL}?${params.toString()}`,
    { signal }
  );

  if (!response.ok) {
    throw new Error(
      `${packageType} search failed with status ${response.status}`
    );
  }

  const data = (await response.json()) as SearchApiResponse;
  const restriction = data.metadata?.restrictionInfo;
  const isRestricted = Boolean(
    restriction && restriction.restricted_type !== 'ok'
  );

  return {
    items: (data.response ?? [])
      .map((item) => toSearchResultItem(item, packageType))
      .filter((item): item is SearchResultItem => item !== null),
    restrictionMessage: isRestricted
      ? restriction?.message?.text || null
      : null,
  };
};

interface SearchItemsParams {
  query: string;
  signal?: AbortSignal;
}

/**
 * Search across published GenAI Studio presets, scoped to this miniapp by
 * `source`. Every hit carries the preset that produced it in
 * `source_info.app_payload.presetId` — that's what makes a result tappable
 * back into its preset feed.
 *
 * Photo and video are separate `package_type` queries, so both run in parallel
 * and the results concatenate (videos first — this miniapp is video-led). One
 * failing package doesn't sink the other; only a total failure throws, so
 * React Query can tell a broken search from one that matched nothing.
 */
const searchItems = async ({
  query,
  signal,
}: SearchItemsParams): Promise<SearchResults> => {
  const settled = await Promise.allSettled(
    [SearchPackageType.Video, SearchPackageType.Photo].map((packageType) =>
      fetchPackage({ query, packageType, signal })
    )
  );

  const fulfilled = settled.filter(
    (result): result is PromiseFulfilledResult<SearchResults> =>
      result.status === 'fulfilled'
  );

  if (!fulfilled.length) {
    throw new Error('Search failed for every package type');
  }

  return {
    items: fulfilled.flatMap(({ value }) => value.items),
    restrictionMessage:
      fulfilled.find(({ value }) => value.restrictionMessage)?.value
        .restrictionMessage ?? null,
  };
};

export default searchItems;
